import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { Asset, Entry } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the Contentful order status progress step icons per order channel,
 * shared by the order status screen and the order status live activity.
 * Steps without an icon are left out, so they render the default progress dot.
 */
export const useOrderStatusProgressStepIcons = () => {
  const { data } = useContentfulContentTypeEntry<LiveActivityWidgetContent>({
    contentType: LIVE_ACTIVITY_WIDGET_CONTENT_TYPE,
    include: 2,
  });

  // ─── Derived ─────────────────────────────────────────────────────────────

  const fields = data?.fields;

  // NOTE: Icons must follow the order of the order status progress steps.
  const progressStepIcons = useMemo(
    () => ({
      pickup: getProgressStepIcons([
        fields?.pickupReceivedIcon,
        fields?.pickupPreparingIcon,
        fields?.pickupCompleteIcon,
      ]),
      outpost: getProgressStepIcons([
        fields?.outpostReceivedIcon,
        fields?.outpostDeliveringIcon,
        fields?.outpostCompleteIcon,
      ]),
      delivery: getProgressStepIcons([
        fields?.deliveryReceivedIcon,
        fields?.deliveryPreparingIcon,
        fields?.deliveryReadyForCourierIcon,
        fields?.deliveryDeliveringIcon,
      ]),
    }),
    [fields],
  );

  return progressStepIcons;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getProgressStepIcons(iconEntries: (ImageEntry | undefined)[]) {
  return iconEntries.flatMap(getProgressStepIcon);
}

function getProgressStepIcon(iconEntry: ImageEntry | undefined, stepIndex: number) {
  // NOTE: Unresolved links (e.g. unpublished entries) come back without fields.
  const src = iconEntry?.fields?.image?.fields?.file?.url;

  if (!iconEntry || !src) {
    return [];
  }

  const alt = iconEntry.fields.image.fields.description;
  const width = iconEntry.fields.width ?? DEFAULT_ICON_SIZE;
  const height = iconEntry.fields.height ?? DEFAULT_ICON_SIZE;

  return [{ stepIndex, src, alt, width, height }];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LIVE_ACTIVITY_WIDGET_CONTENT_TYPE = 'orderLiveActivityWidget';

/**
 * Render size (in points) used when the Contentful image entry has no size.
 */
const DEFAULT_ICON_SIZE = 24;

// ─── Types ───────────────────────────────────────────────────────────────────

type LiveActivityWidgetContent = {
  pickupReceivedIcon?: ImageEntry;
  pickupPreparingIcon?: ImageEntry;
  pickupCompleteIcon?: ImageEntry;
  outpostReceivedIcon?: ImageEntry;
  outpostDeliveringIcon?: ImageEntry;
  outpostCompleteIcon?: ImageEntry;
  deliveryReceivedIcon?: ImageEntry;
  deliveryPreparingIcon?: ImageEntry;
  deliveryReadyForCourierIcon?: ImageEntry;
  deliveryDeliveringIcon?: ImageEntry;
};

type ImageEntry = Entry<ImageContent>;

type ImageContent = {
  id: string;
  image: Asset;
  width?: number;
  height?: number;
};
